import { AnalyticsBrowser } from '@segment/analytics-next';
import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';

import { useLeadContext } from './LeadProvider';

type AnalyticsPayload = Record<string, string | number | string[]>;

export interface AnalyticsContextType {
  track: (eventName: string, payload?: AnalyticsPayload) => Promise<void | null>;
  identify: (userId: string, traits?: Record<string, string>) => Promise<void | null>;
  page: (name: string, properties?: AnalyticsPayload) => Promise<void | null>;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
  track: async () => null,
  identify: async () => null,
  page: async () => null,
});

const VITE_SEGMENT_KEY = import.meta.env.VITE_SEGMENT_KEY;

export default function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { lead } = useLeadContext();
  const analytics = useMemo(() => AnalyticsBrowser.load({ writeKey: VITE_SEGMENT_KEY }), []);

  const analyticsContext: AnalyticsContextType = useMemo(
    () => ({
      track: async (eventName: string, payload?: AnalyticsPayload) => {
        await analytics.track(eventName, payload);
      },
      identify: async (userId: string, traits?: Record<string, string>) => {
        await analytics.identify(userId, traits ?? {});
      },
      page: async (name: string, properties?: AnalyticsPayload) => {
        await analytics.page(name, properties);
      },
      // can add more segment methods here e.g identify, trackLink, page etc...
    }),
    [analytics],
  );

  useEffect(() => {
    // we may need to dispatch identify as well when lead is not provided
    // otherwise anonymousId will be sent to segment
    if (lead) {
      analyticsContext.identify(lead?.universalId?.uid ?? '');
    } else {
      analytics.reset();
    }
  }, [lead]);

  return <AnalyticsContext.Provider value={analyticsContext}>{children}</AnalyticsContext.Provider>;
}

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);

  if (context === undefined) {
    throw new Error('useAnalyticsContext must be used within a AnalyticsProvider');
  }

  return context;
};
